import React, { Suspense, useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { css } from '@emotion/core'
import { NoticeLoading, ControlView, ControlHide } from '@fs/zion-icon'
import { SingleImageViewer } from '@fs/zion-image-viewer'
import { useFeatureFlags } from '@fs/zion-flags'
import { Title } from '@fs/zion-head'
import { getUser } from '@fs/zion-user'
import { useOverlay, ZoomPanControlButton, usePageWidth, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { useQueryParams } from '@fs/zion-router'
import zionDebug from '@fs/zion-debug'
import { Usabilla } from '@fs/zion-usabilla'
import isEmpty from 'lodash.isempty'
import { encodeArk } from '@fs/zion-ark'
import {
  addAppAttribution,
  addTypeDataToElements,
  convertToSLSRectangle,
  generateSls,
  getMostRecentFieldValue,
  getNestedChildren,
  getNestedSubelementsAndFloatingElementsByType,
  processSlsElements,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
  useSlsTypes,
} from '@fs/zion-sls'
import { saveTaskEdits, taskCompleted } from '../../../shared/services/recordEnhancement/recordEnhancementService'
import FeedbackOverlay from '../../../shared/components/feedbackOverlay/FeedbackOverlay'
import HeaderBar from './components/headerBar/HeaderBar'
import WelcomeOverlay from './components/welcomeOverlay/WelcomeOverlay'
import { addNAMEAttribution } from './utils/saveFunctions'
import { headers, FullNameApp } from '../../../shared/services/config'
import { useFullNameDispatchContext, useFullNameStateContext } from './providers/FullNameContext'
import { onRectangleDrawn } from '../../utils/imageHighlightUtils'
import ErrorOverlay from '../../../shared/components/errorOverlay/ErrorOverlay'
import useTasks from '../../hooks/useTasks'
import { getZoom, getPan } from '../../utils/zoomPanUtils'
import TasksDoneOverlay from '../../components/sharedReviewComponents/tasksDoneOverlay/TasksDoneOverlay'
import { getAttachedRects, goBackToGive, getImagePermission } from '../../utils/reviewUtils'
import { getImageArkUrl } from '../../../shared/services/das/dasService'
import ReviewCompletionDialog from '../../components/sharedReviewComponents/ReviewCompletionDialog/ReviewCompletionDialog'
import ReviewPanel from './components/reviewPanel/ReviewPanel'
import useStepper from '../../hooks/useStepper'
import SlsHighlightsBase from '../../components/slsHighlightsBase/SlsHighlightsBase'
import { nameFieldTypes } from './utils/fullNameCorrectionUtils'
import useSkipImageOverlay from '../../components/sharedReviewComponents/skipImageOverlay/useSkipImageOverlay'
import SkipImageOverlay from '../../components/sharedReviewComponents/skipImageOverlay/SkipImageOverlay'
import usePrisonAccess from '../../../Give/hooks/usePrisonAccess'
import { useR2Highlights } from '../../providers/r2Provider/R2Context'

const layoutCss = css`
  display: flex;
  flex-direction: column;
  // This fixes the header and the bottom docked infosheet covering the image viewer
  flex-grow: 1;
`

const debug = zionDebug('rip:FullNameCorrection') // to turn this on do this in the console: localStorage.debug = 'fs:rip:FullNameCorrection'

const FullNameCorrection = () => {
  usePageView({ page_channel: 'RIP', page_detail: 'Full Name Corrections' })
  const { query } = useQueryParams()
  const { task: taskTypeParam, placeCode: placeCodeStr, debugArk } = query
  const usingDebugArk = useMemo(() => Boolean(debugArk), [debugArk])

  const placeCode = parseInt(placeCodeStr, 10)
  const [t] = useTranslation()
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag, rip_sharedImageViewer_feedback_FullName: feedbackFlag } =
    useFeatureFlags([
      'rip_sharedImageViewer_saveChanges',
      'rip_sharedImageViewer_statsTracking',
      'rip_sharedImageViewer_feedback_FullName',
    ])
  const state = useFullNameStateContext()
  const dispatch = useFullNameDispatchContext()
  const zionSlsState = useSlsStateContext()
  const zionSlsDispatch = useSlsDispatchContext()
  const [error, setError] = useState()
  const [namesToSkipDispatches, setNamesToSkipDispatches] = useState([])

  const [hasCheckedImagePermission, setHasCheckedImagePermission] = useState(false)

  const savedSkippedNames = useRef(false)
  const { tasksMarkedNotAPerson } = state

  const stepper = useStepper({ steps: 4 })

  // / Zion overlays and refs / //
  const finishedOverlay = useOverlay()
  const welcomeOverlay = useOverlay()
  const [taskErrorMessage, setTaskErrorMessage] = useState()
  const [completedName] = useState('') // Stash the name information from state.stepFour for the ReviewCompletionDialog
  const pageWidth = usePageWidth()
  const isMobile = pageWidth({ default: true, md: false })
  const [showRecordDetails, setShowRecordDetails] = useState(false)
  const h = useSlsHighlightsContext()
  const { displayHighlights, displayHighlightsApi } = useR2Highlights()
  const { accessFromPrison, isReady } = usePrisonAccess()

  const { data: dataTypes } = useSlsTypes({
    fsUserAgentChain: headers['FS-User-Agent-Chain'],
    dispatchTypes: false,
    lang: 'en',
  })

  const currentTaskUpdated = useCallback(
    ({ newTask, imageHighlights }) => {
      if (!imageHighlights) imageHighlights = h.getHighlights().mainEditHighlights

      const currentNameParts = {}
      const highlightIds = []

      if (!isEmpty(newTask?.subElements) && imageHighlights?.length) {
        // Task subelements are Given name and Surname,
        if (newTask?.subElements?.length) {
          newTask.subElements.forEach((element) => {
            // Each name task element can have multiple sub elements, like prefix, sufix, etc.
            if (element?.subElements?.length) {
              element.subElements.forEach((subElement) => {
                const highlightsOnField = imageHighlights.filter((highlight) => highlight.fieldId === subElement.id)
                const fields = []

                highlightsOnField.forEach((highlight, index) => {
                  highlightIds.push(highlight.id)
                  fields.push({
                    elementType: subElement.elementType,
                    partitionKey: subElement.partitionKey,
                    fieldId: subElement.id,
                    superElements: [...subElement.superElements],
                    fieldType: subElement.fieldType,
                    // SortableContext in StepThree.js requires that these fields have an 'id' attribute, so we can't use something like 'highlightId'
                    id: highlight.id,
                    rects: [convertToSLSRectangle(highlight)],
                    typeData: { ...subElement.typeData },
                    confidence: subElement.confidence,
                    // originalValue is the value of the field before being modified by the user
                    // It is needed to do a diff between the original value and the new value when the data is being saved
                    // It is not part of the SLS spec and will be stripped from the payload submitted to SLS
                    originalValue: subElement?.fieldValues?.[0]?.origValue?.text || '',
                    userEnteredValue: index === 0 ? getMostRecentFieldValue(subElement) || '' : '',
                  })
                })

                if (fields.length) {
                  currentNameParts[subElement.fieldType] = { fields }
                }
              })
            }
          })
        }

        // Sort the child elements of currentNameParts by fieldType
        const sortedCurrentNameParts = Object.fromEntries(
          Object.entries(currentNameParts).sort((fieldA, fieldB) => {
            return nameFieldTypes.indexOf(fieldA[0]) - nameFieldTypes.indexOf(fieldB[0])
          })
        )

        dispatch({ type: 'SET_CURRENT_NAME_PARTS', value: sortedCurrentNameParts })
      }
    },
    [dispatch, h]
  )

  const artifactId = useRef(null)
  const artifactUrl = useRef(null)

  /**
   * @typedef {Object} generateTasksParams
   * @property {Object} data - The data from the request
   * @property {Array} images - The images array from the request
   * @property {number} currentIndex - The index of the current task
   * @property {Object} typeData - The type data from SLS (useTasks is in charge of loading types and THEN loading image data)
   *
   * @param {generateTasksParams} params
   */
  const generateTasks = useCallback(
    ({ data, images = [], currentIndex = 0 }) => {
      // Either use "data" from a request, or SLS state for updating tasks after a save
      let sortedPersons = []
      if (data?.slsImageInfo && data?.artifactId && data?.slsImageInfo?.elements) {
        artifactId.current = data.artifactId
        artifactUrl.current = data.artifactUrl
        // Process the data into an images array format
        images = processSlsElements([
          {
            elements: data.slsImageInfo.elements,
            danglingSupers: data.slsImageInfo.danglingSupers,
            danglingElementTypes: data.slsImageInfo.danglingElementTypes,
            danglingElementDescriptions: data.slsImageInfo.danglingElementDescriptions,
            partitionKey: encodeArk(artifactId.current),
          },
        ]).images
        // Get the sort order of the persons
        const personIds = data.slsImageInfo.elements?.filter((el) => el.elementType === 'PERSON').map((p) => p.id)
        sortedPersons = [{ partitionKey: data.slsImageInfo.elements[0].partitionKey, personIds: personIds || [] }]
      }

      addTypeDataToElements(images, dataTypes, t('type-unknown.label', '<Unknown Type>'))

      const namesToSkip = []
      const counts = { hasNoRects: 0, hasAttribution: 0, isNameField: 0 }
      // Check each of the NAME's subElements, ensuring that at least one of them is a valid name part
      const outNamesWithoutValidChildren = (nameElement) => {
        const validElementTypes = [
          'NAME_GIVEN',
          'NAME_SURNAME',
          'NAME_SUFFIX',
          'NAME_PREFIX',
          'NAME_SUFFIX_PREFIX',
          'NAME_PATRONYMIC',
          'NAME_MATRONYMIC',
        ]
        const hasSomeValidChildren = nameElement?.subElements?.some((subElement) =>
          validElementTypes.includes(subElement.elementType)
        )
        if (hasSomeValidChildren) return true
        namesToSkip.push(nameElement)
        return false
      }

      /**
       * NAME element GAME PLAN:
       * 1. Get all the NAME elements
       * 2. Either include the NAME element in the experience, or add attribution to it via the 'namesToSkip' array
       *    In this way, a task given to Full Name should only come back incomplete if the user stops halfway through
       * 3. Always filter out the follwoing from the user's experience (adding attribution to everything that is skipped):
       *   - NAME fields (we only want NAME elements)
       *   - NAME elements with no valid children (outNamesWithoutValidChildren)
       *   - NAME elements with no highlights on their subElements
       * 4. If using the debugArk, allow NAME elements that already have user attribution (gone through this process before)
       */

      // We need to order the name elements the same way the persons are ordered so everytime task are regenerated we get the same order and can move to the next or previous tasks
      const allNAME_Elements = getNestedSubelementsAndFloatingElementsByType(
        images[0] || zionSlsState?.images?.[0],
        'NAME'
      )
      const personIdsOrder = sortedPersons[0]?.personIds || zionSlsState?.personOrder[0]?.personIds || []
      const sortedAllNAME_Elements = allNAME_Elements.sort((a, b) => {
        return personIdsOrder.indexOf(a?.personId) - personIdsOrder.indexOf(b?.personId)
      })

      const nameElements = sortedAllNAME_Elements
        .filter(outNamesWithoutValidChildren)
        .filter((element) => {
          // Words are easier to read than functions, I think
          const hasSomeRects = Boolean(getNestedChildren(element)?.find((child) => child.rects?.length))
          const hasNoRects = !hasSomeRects
          const lacksAttribution = Boolean(!element.attribution?.userId?.startsWith('cis'))
          const hasAttribution = !lacksAttribution
          const isNameField = element.elementType === 'FIELD'
          // Update counts:
          if (debug) {
            if (hasNoRects) counts.hasNoRects++
            if (hasAttribution) counts.hasAttribution++
            if (isNameField) counts.isNameField++
          }
          if (isNameField) return false // Don't want any NAME fields.  Just NAME element structures
          // We want to skip names with no highlights, and add attribution to the skipped names so we don't get them again
          if (hasNoRects && lacksAttribution) {
            namesToSkip.push(element)
            return false
          }
          if (usingDebugArk) return hasSomeRects
          return hasSomeRects && lacksAttribution
        })
        .map((x) => {
          let personId
          if (x?.superElements?.length) personId = x.superElements[0].id

          return {
            ...x,
            artifactUrl: artifactUrl?.current || getImageArkUrl(x.partitionKey),
            apid: artifactId?.current,
            subElements: x.subElements.filter((e) => e.elementType !== 'FIELD'),
            personId,
            notAPerson: tasksMarkedNotAPerson.includes(x.id),
          }
        })

      // eslint-disable-next-line no-warning-comments
      // This is a hack to stop images that are already done from being reloaded (because they weren't properly marked as done, due to a bug in the past)
      if (!usingDebugArk && allTasksCompletedAlready(nameElements)) {
        taskCompleted(artifactId.current, true, 'Full Name Review')
        setTaskErrorMessage({
          title: t('tasks-done-overlay.no-more-tasks.error.title', 'No more tasks'),
          message: t('tasks-done-overlay.tasks-done.error.message', 'Go back to Get Involved to do another search.'),
        })
      }

      if (nameElements.length) {
        if (!hasCheckedImagePermission) {
          getImagePermission(nameElements, FullNameApp).then((hasPermission) => {
            setShowRecordDetails(hasPermission)
            setHasCheckedImagePermission(true)
          })
        }

        // Reinitialize the highlights hook with the new data from the updated images
        const { mainHighlights: imageHighlights } = h.init({ mainImageArk: encodeArk(artifactId.current), images })

        // Add attribution to the names we will skip
        let dispatches = []
        if (namesToSkip.length) {
          namesToSkip.forEach((name) => {
            dispatches = [...dispatches, ...addNAMEAttribution([], [], name, saveChangesFlag.isOn)]
          })
        }

        setNamesToSkipDispatches(dispatches)
        currentTaskUpdated({ imageHighlights, newTask: nameElements[currentIndex] })
      }

      // set data in sls state
      zionSlsDispatch({
        type: 'BATCH_DISPATCHES',
        value: [
          { type: 'SET_DATA', value: { images, currentImageArk: encodeArk(artifactId.current) } },
          { type: 'ADD_PERSON_ORDER', value: sortedPersons },
          { type: 'SORT_PERSONS' },
        ],
      })

      return nameElements
    },
    [
      usingDebugArk,
      currentTaskUpdated,
      tasksMarkedNotAPerson,
      zionSlsState.images,
      zionSlsState.personOrder,
      dataTypes,
      t,
      zionSlsDispatch,
      hasCheckedImagePermission,
      h,
      saveChangesFlag.isOn,
    ]
  )

  const onGoalCompleted = useCallback(() => {
    finishedOverlay.open()
  }, [finishedOverlay])

  const useTasksMethods = useTasks({
    currentTaskUpdated,
    generateTasks,
    goalDefault: 1,
    goalCacheKey: 'fullNameReviewTaskLengthPreference',
    onGoalCompleted,
    setError,
    types: dataTypes,
  })

  const { currentTask, doneCount, goal, loading, continueWithNewGoal, goToNextTask, getNewImage } = useTasksMethods

  const saveSls = useCallback(
    async (dispatches, updateState = true) => {
      const sls = generateSls(dispatches)
      addAppAttribution(sls, state.userId, FullNameApp)
      if (saveChangesFlag.isOn) {
        if (sls?.length) {
          debug('saveSls: ', { sls })
          await saveTaskEdits(zionSlsState.currentImageArk, sls, false, false, taskTypeParam)
          if (updateState) {
            zionSlsDispatch({
              type: 'BATCH_DISPATCHES',
              value: dispatches,
            })
          }
        }
      } else {
        debug('Saving changes to SLS is disabled')
      }
    },
    [saveChangesFlag.isOn, state.userId, taskTypeParam, zionSlsState.currentImageArk, zionSlsDispatch]
  )

  // Add attribution to the skipped tasks so the completion check passes.  Don't update state because it will add a duplicate ADD_SUB
  // SLS doesn't add a duplicate ADD_SUB, they just add the attribution.
  if (!savedSkippedNames.current && namesToSkipDispatches?.length) {
    saveSls(namesToSkipDispatches, false)
    savedSkippedNames.current = true
  }

  useEffect(() => {
    getUser().then((u) => dispatch({ type: 'SET_USER_ID', value: u.cisId }))
  }, [dispatch])

  const rectangleDrawnHandler = (position) => {
    onRectangleDrawn(
      position,
      [currentTask],
      zionSlsState.currentImageArk,
      state,
      displayHighlightsApi,
      dispatch,
      h.activeFieldInfo,
      true,
      false
    )

    h.setCreatingRects(false)
    dispatch({ type: 'SET_USER_PERFORMED_FIX' })
  }

  const handleContinue = useCallback(
    (newGoal) => {
      continueWithNewGoal(newGoal)
      finishedOverlay.close()
      stepper.goToStep(1)
    },
    [continueWithNewGoal, finishedOverlay, stepper]
  )

  const controls = () => (
    <>
      {stepper.currentStep === 2 && !h.isCreatingRects && (
        <ZoomPanControlButton
          Icon={!displayHighlights?.isShowingAll ? ControlView : ControlHide}
          onClick={() => {
            displayHighlightsApi.showAll(!displayHighlights.isShowingAll)
          }}
          label={
            displayHighlights?.isShowingAll
              ? t('shared-image-viewer.hide.button-label', 'Hide Highlights')
              : t('shared-image-viewer.show.button-label', 'Show Highlights')
          }
          linkName="show-hide-highlights-button"
        />
      )}
    </>
  )

  if (/NoMoreTasks/.test(error?.message) || /NoNamesToReview/.test(error)) {
    if (!taskErrorMessage) {
      setTaskErrorMessage({
        title: t('tasks-done-overlay.no-more-tasks.error.title', 'No more tasks'),
        message: t('tasks-done-overlay.tasks-done.error.message', 'Go back to Get Involved to do another search.'),
      })
    }
  }

  if (/TaskExpired/.test(error?.message)) {
    if (!taskErrorMessage) {
      setTaskErrorMessage({
        title: t('tasks-done-overlay.tasks-done.error.title', 'Task has expired'),
        message: t('tasks-done-overlay.tasks-done.error.message', 'Go back to Get Involved to do another search.'),
      })
    }
  }

  const handleSkipTask = useCallback(() => {
    goToNextTask(true)
  }, [goToNextTask])

  const { handleSkip, handleClickNewImage, resetSkipCounter, skipImageOverlay } = useSkipImageOverlay({
    onSkip: handleSkipTask,
    onGetNewImage: getNewImage,
    currentTask,
  })

  debug('FullNameCorrection Render: ', {
    doneCount,
    currentTask, // use this instead of tasks[index] if we get rid of the tasks state in this file
    loading,
    error,
    state,
    zionSlsState,
    displayHighlights,
    tasksMarkedNotAPerson,
    useTasksMethods,
  })

  return (
    <Suspense fallback={<NoticeLoading />}>
      <Title>{t('task-viewer.full-name-review-title', 'Full Name Review')}</Title>
      <div css={layoutCss}>
        <Separator size="nano" />
        <HeaderBar
          loading={loading}
          personId={currentTask?.personId}
          doneCount={doneCount}
          maxTasks={goal}
          showRecordDetails={showRecordDetails}
        />
        <Separator size="nano" />
        {currentTask?.partitionKey && currentTask?.artifactUrl && (
          <SingleImageViewer
            {...h.zoomPanRefs}
            initialZoom={getZoom(getAttachedRects(displayHighlights), isMobile)}
            initialPanX={getPan(getAttachedRects(displayHighlights)).panX}
            initialPanY={getPan(getAttachedRects(displayHighlights)).panY}
            src={currentTask.artifactUrl}
            controls={controls()}
            drawingMode={h.isCreatingRects}
            onRectangleDrawn={rectangleDrawnHandler}
            prompt={h.isCreatingRects ? t('draw-highlight.body', 'Click anywhere and drag to create a highlight') : ''}
          >
            <SlsHighlightsBase showEditHighlights={stepper.currentStep === 2} state={state} dispatch={dispatch} />
          </SingleImageViewer>
        )}
      </div>
      {currentTask && (
        <>
          <ReviewPanel
            useTasksMethods={useTasksMethods}
            stepper={stepper}
            types={dataTypes}
            finishedOverlay={finishedOverlay}
            generateTasks={generateTasks}
            setError={setError}
            handleSkip={handleSkip}
            resetSkipCounter={resetSkipCounter}
          />

          <ReviewCompletionDialog
            dialogOverlay={finishedOverlay}
            initialChipValue="5names"
            chipOptions={[
              {
                label: t('task-viewer.finished-modal.option.5names', '5 names'),
                value: '5names',
                max: 5,
                selected: true,
              },
              { label: t('task-viewer.finished-modal.option.10names', '10 names'), value: '10names', max: 10 },
              { label: t('task-viewer.finished-modal.option.20names', '20 names'), value: '20names', max: 20 },
              { label: t('task-viewer.finished-modal.option.nolimit', 'No limit'), value: 'nolimit', max: null },
            ]}
            totalFinished={doneCount + 1}
            handleContinue={handleContinue}
            goBackToGive={() => goBackToGive(accessFromPrison && isReady, 'fullNameReview')}
            placeCode={placeCode}
            name={completedName}
          />

          <WelcomeOverlay
            welcomeOverlay={welcomeOverlay}
            goBackToGive={() => goBackToGive(accessFromPrison && isReady, 'fullNameReview')}
          />
          <SkipImageOverlay overlay={skipImageOverlay} handleClickNewImage={handleClickNewImage} />
        </>
      )}
      <ErrorOverlay error={error} setError={setError} />

      {taskErrorMessage && (
        <TasksDoneOverlay
          title={taskErrorMessage.title}
          message={taskErrorMessage.message}
          goBackToGive={() => goBackToGive(accessFromPrison && isReady, 'fullNameReview')}
        />
      )}
      {!isMobile && <Usabilla url="//w.usabilla.com/d90ab84f6f51.js" hideOnMobile mobileBreakpoint="md" />}
      {!isMobile && feedbackFlag.isOn && <FeedbackOverlay />}
    </Suspense>
  )
}

function allTasksCompletedAlready(nameElements) {
  return nameElements.every((nameElement) => nameElement.attribution?.userId?.startsWith('cis'))
}

export default FullNameCorrection
